import { IWebVersion } from './interfaces';

/** Base Javascript Utilities wOut any external References */

//#region Utility Interfaces

/** Helper Interface for String Associative Container */
export type INamed<T> = Record<string, T>;

/** Possibility to set relative Path to root from Current SPA Page  (for tests) */
export const PageConfig = {
    // Relative Path To Root
    RelPath: "",
    // if RelPath is Set, e.g '/dist/rwf/' WITH ending slash
    FromRoot: "",
};

/** Adjust a Path:
* - relative to current spa if given
* - relative root
*/
export function FixupRelPath(path: string) {
    if (typeof path !== "string" || path.match(/^https?:/i))
        return path;

    if (path && path[0] == '#' && PageConfig.FromRoot) {
        return PageConfig.FromRoot + path;
    }
    let relPath = PageConfig.RelPath,
        isSlash = path[0] == "/";
    if (relPath) {
        path = (isSlash ? "/" : "") + relPath + (isSlash ? "" : "/") + path;
    }

    if (isSlash)
        path = "." + path;
    return path;
}

/** Determine Version Info by from tags on main page - convention */
export function GetVersionInfo(fallback?: IWebVersion) {
    var attr = (name: string, def: string) => (document.head.attributes.getNamedItem(name) || { value: def }).value;
    fallback = fallback || { Version: '<unbekannt>',
                            PatchLevel: '<unbekannt>',
                            MaxBuild: '<unbekannt>',
                            MinBuild: '<unbekannt>',
                            Release: '<unbekannt>' };
    try {
        fallback.Version = attr("data-infoma-appversion", fallback.Version || "");
        fallback.PatchLevel = attr("data-infoma-patchlevel", fallback.PatchLevel || "");
        fallback.MaxBuild = attr("data-infoma-maxbuild", fallback.MaxBuild || "");
        fallback.MinBuild = attr("data-infoma-minbuild", fallback.MinBuild || "");
        fallback.Release = attr("data-infoma-release", fallback.Release || "");
    } catch (err) {
    }
    return fallback;
}

/** Description of a UI Bindable Operation */
export interface IInfOperation {

    /** Marker Operation is Currently In Progress
     * TODO : Present in Visualzation
      */
    IsRunning?: boolean;

    /** Operation should currently not be visualized
     * either boolean: or string "Enabled" : Visible when Enabled
        Remark  Started to move this property (="Enabled") from here to Usage (i.e. inf-operation directice as --> inf-operation-options="{ Hidden : 'Enabled'}"
        --- could(, and should) better be done there
    */
    Hidden?: boolean | "Enabled";

    /** Operation is Enabled */
    Enabled?: boolean;

    /** TODO : Present in Visualization */
    Checked?: boolean;

    /** Trigger Operation (return value is Optional) */
    Execute(...args): PromiseLike<any> | void;

    /** TODO : Unify ... */
    Icon?: any;

    Title?: string;

    name?: string;
}

//#endregion

export namespace fn {
    export function nullOp(...args): any;
    /** nullOp Function Helper */
    export function nullOp() { }

    /** noop Function Helper */
    export const noop = <(...args) => any>nullOp;

    /** filter eval as boolean Helper */
    export function isSet(arg: any) { return !!arg; }

    /** Identity Function Helper */
    export function identity<T>(item: T) { return item; }

    /** Combine Array of Functions to Function Excuting all (by cloning input Func array)*/
    export function combine<TFunc extends Function>(items: TFunc[]): () => void;
    export function combine<TResult, TFunc extends () => TResult>(items: TFunc[]): () => TResult[];
    export function combine<TResult, TFunc extends Function>(items: TFunc[], ...args) {
        var clonedItems = new Array(...items);
        return () => clonedItems.map(c => c.call(undefined, ...args));
    }

    /** Call Factory N Times */
    function times<T>(n: number, iterator: () => T) {
        var result = new Array<T>(n);
        for (var i = 0; i < n; i++)
            result[i] = iterator();
        return result;
    }

    /** random string of characters(lowercase) / numbers * /
     * @param len length of string (default : 12)
     */
    export function id(len?: number) {
        var len = len > 0 ? len : 12;
        // Ensure first char is character, build to desired length
        var result = _chars[Math.floor(Math.random() * _firstNr)] +
            times(len - 1, () => _chars[Math.floor(Math.random() * _charLen)]).join("");

        return result;
    }
    const _chars = "abcdefghijklmnopqrstuvwxyz0123456789",
        _firstNr = _chars.indexOf("0"),
        _charLen = _chars.length;

    export function GuidEqual(id1: string, id2: string) {
         if (id1 === id2) {
             return true;
        }

        if (!id1 || !id2) {
             return false;
        }

        return ("" + id1).replace(reNormalizeGuid, "").toLowerCase() === ("" + id2).replace(reNormalizeGuid, "").toLowerCase();
    }
    const reNormalizeGuid = /[{}-]/g;

    /** Usage:
    * var data = { 'first name': 'George', 'last name': 'Jetson', 'age': 110 };
    * var querystring = EncodeQueryData(data);
    */
    export function EncodeQueryData(data: { [name: string]: any }) {
        var ret = [];
        for (var d in data) {
            var value = data[d];
            if (value != undefined && value != null) {
                ret.push(encodeURIComponent(d) + "=" + encodeURIComponent(value));
            }
        }
        return ret.join("&");
    }

    // public method for encoding
    export function toBase64(input: string) {
        var output = "";
        var chr1, chr2, chr3, enc1, enc2, enc3, enc4;
        var i = 0;

        input = _utf8_encode(input);

        while (i < input.length) {

            chr1 = input.charCodeAt(i++);
            chr2 = input.charCodeAt(i++);
            chr3 = input.charCodeAt(i++);

            enc1 = chr1 >> 2;
            enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
            enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
            enc4 = chr3 & 63;

            if (isNaN(chr2)) {
                enc3 = enc4 = 64;
            } else if (isNaN(chr3)) {
                enc4 = 64;
            }

            output = output +
                _keyStr.charAt(enc1) + _keyStr.charAt(enc2) +
                _keyStr.charAt(enc3) + _keyStr.charAt(enc4);

        }

        return output;
    }
    // private property
    const _keyStr = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";
    // private method for UTF-8 encoding
    function _utf8_encode(input: string) {
        var utftext = "";

        for (var n = 0; n < input.length; n++) {

            var c = input.charCodeAt(n);

            if (c < 128) {
                utftext += String.fromCharCode(c);
            }
            else if ((c > 127) && (c < 2048)) {
                utftext += String.fromCharCode((c >> 6) | 192);
                utftext += String.fromCharCode((c & 63) | 128);
            }
            else {
                utftext += String.fromCharCode((c >> 12) | 224);
                utftext += String.fromCharCode(((c >> 6) & 63) | 128);
                utftext += String.fromCharCode((c & 63) | 128);
            }

        }

        return utftext;
    }

    /** Shorthand for Convert const to Function returning const */
    export function ToFunc<T>(val: T) {
        return () => val;
    }

    /** Clone to new 'real' Array */
    export function ToArray<T>(items: ArrayLike<T>) {
        var result = <T[]>[];
        for (var i = 0; i < items.length; i++)
            result.push(items[i]);
        return result;
    }

    /** Outer Join tow Containers by (unique) keys */
    export function OuterJoin<T, TResult>(t1: ArrayLike<T>, t2: ArrayLike<T>, getKey: (item: T) => string, callback: (t1: T, t2: T, key: string) => TResult): TResult[] {
        var items2 = ToDictionary(t2, getKey);
        var result = <TResult[]>[];
        t1 = ToArray(t1); // Clone (Manipulations!)
        for (var i = 0; i < t1.length; i++) {
            var item1 = t1[i];
            var key = getKey(item1);
            result.push(callback(item1, items2[key], key));
            delete items2[key];
        }
        Object.getOwnPropertyNames(items2).forEach(key => result.push(callback(undefined, items2[key], key)));
        return result;
    }

    /** group items by projection to name */
    export function GroupBy<T>(items: ArrayLike<T>, getName: keyof T): INamed<T[]>;
    export function GroupBy<T>(items: ArrayLike<T>, getName: (item: T) => string): INamed<T[]>;
    export function GroupBy<T>(items: ArrayLike<T>, getName: keyof T | ((item: T) => string)): INamed<T[]> {
        var result: INamed<T[]> = {};
        const get = getter(getName);
        ToArray(items).forEach(item => {
            var name = get(item),
                arr = result[name] || (result[name] = []);
            arr.push(item);
        });
        return result;
    }

    /** Convert to Enum to Dictionary by Mapping item to Key */
    export function ToDictionary<T>(items: ArrayLike<T>, getName: keyof T): INamed<T>;
    export function ToDictionary<T>(items: ArrayLike<T>, getName: ((item: T) => string)): INamed<T>;
    export function ToDictionary<T>(items: ArrayLike<T>, getName: keyof T | ((item: T) => string)): INamed<T> {
        var result = <INamed<T>>{};
        var len = items.length;
        if (len > 0) {
            var get = getter(<any>getName);
            for (var i = 0; i < len; i++) {
                var item = items[i];
                if (item) {
                    result[<any>get(item)] = item;
                }
            }
        }
        return result;
    }

    /**
     * Utility Function to allow compile time error catching on changes on "enumerable" Types
     * @param obj
     * @param msg If set throw error, otherwise silently continue
     */
    export function never(obj: never, msg?: string | Error) {
        if (msg)
            throw (typeof (msg) === "string" ? new Error(msg) : msg);
    }

    export function Filter<T, TKey extends keyof T>(items: ArrayLike<T>, key: TKey, value: T[TKey]) {
        var len = items && items.length,
            result = new Array<T>();
        if (!(len > 0))
            return result;

        for (var i = 0; i < len; i++)
            if (items[i][key] == value)
                result.push(items[i]);

        return result;
    }

    /** Find First by Condition On Array like object */
    export function FindFirst<T, TValue extends keyof T>(items: ArrayLike<T>, propName: TValue, value?: T[TValue]): T;
    export function FindFirst<T>(items: ArrayLike<T>, validator: (item: T, idx: number) => boolean): T;
    export function FindFirst<T>(items: ArrayLike<T>, validatorOrProp: ((item: T, idx: number) => boolean) | string, value?: any): T {
        if (!items || !items.length)
            return;

        if (typeof validatorOrProp == "string") {
            if (arguments.length > 2)
                var validator = (item: T, idx: number) => (item[validatorOrProp] == value);
            else
                var validator = (item: T, idx: number) => !!item[validatorOrProp];
        }
        else
            validator = validatorOrProp;

        for (var i = 0; i < items.length; i++)
            if (validator(items[i], i) == true)
                return items[i];
    }

    /** Remove Items By Condition */
    export function RemoveIf<T>(items: Array<T>, validator: (item: T) => boolean) {
        var i = items && items.length;
        while (i--) {
            if (validator(items[i])) {
                items.splice(i, 1);
            }
        }
        return items;
    }

    /**
    * Reports the index of the first occurrence in this instance of any character in a specified array of Unicode characters.
    * @return the index of the first occurrence in this instance of any character in a specified array of Unicode characters.
    */
    export function indexOfAny(source: string, anyOf: string, startIndex?: number, count?: number): number {
        startIndex = isNaN(startIndex) ? 0 : startIndex;
        if (startIndex < 0) {
            startIndex = 0;
        }

        count = isNaN(count) ? -1 : ((count >= 0) ? count : -1);

        if (anyOf != null && source != null && source != "") {
            var i: number;
            var l /*int*/ = anyOf.length;
            var endIndex /*int*/;
            if ((count < 0) || (count > l - startIndex)) {
                endIndex = l - 1;
            }
            else {
                endIndex = startIndex + count - 1;
            }
            for (i = startIndex; i <= endIndex; i++) {
                if (source.indexOf(anyOf[i]) > - 1) {
                    return i;
                }
            }
        }
        return - 1;
    }

    /** Transform Array to Dictionary by given key- and value- mapping functions */
    export function ToDictionaryEx<T, TValue>(items: ArrayLike<T>, getName: string, getValue: string): INamed<TValue>;
    export function ToDictionaryEx<T, TValue>(items: ArrayLike<T>, getName: string, getValue: ((item: T, key: string) => TValue)): INamed<TValue>;
    export function ToDictionaryEx<T, TValue>(items: ArrayLike<T>, getName: ((item: T) => string), getValue: string): INamed<TValue>;
    export function ToDictionaryEx<T, TValue>(items: ArrayLike<T>, getName: ((item: T) => string), getValue: ((item: T, key: string) => TValue)): INamed<TValue>;
    export function ToDictionaryEx<T, TValue>(items: ArrayLike<T>, getName: string | ((item: T) => string), getValue: string | ((item: T, key: string) => TValue)): INamed<TValue> {
        var result = <INamed<TValue>>{};
        if (items && items.length > 0) {
            var nameGetter = getter(getName),
                valueGetter = getter(getValue);
            for (var i = 0; i < items.length; i++) {
                var item = items[i];
                if (item) {
                    var key = nameGetter(item);
                    result[key] = valueGetter(item, key);
                }
            }
        }
        return result;
    }

    /** transform stringto By PropName Getter */
    function getter<T>(prop: string | ((...args) => T)) {
        var t = typeof prop;
        switch (t) {
            case "function":
                return <(...args) => T>prop;
            case "string":
                return <(...args) => T>function () { return arguments[0][<string>prop]; };
            default:
                throw new Error("Invalid getter type");
        }
    }

    /** Remap Named Dictionary */
    export function Remap<TElement, TKey extends keyof TElement, TTarget>(items: TElement, func: (item: TElement[TKey], name: keyof TElement) => TTarget): { [name in keyof TElement]: TTarget };
    export function Remap<T, TTarget>(items: { [name: string]: T }, func: (item: T, name: string) => TTarget) {
        return ToDictionaryEx(Object.getOwnPropertyNames(items), identity, n => func(items[n], n));
    }

    /** Dictionary To Array */
    export function DictToArray<T>(items: INamed<T>) {
        return Object.getOwnPropertyNames(items).map(item => items[item]);
    }

    /** exclude items from Array */
    export function except<T>(items: T[], except: T[]) {
        items = items.slice();
        except.forEach(e => {
            var idx = items.indexOf(e);
            if (idx >= 0)
                items.splice(idx, 1);
        });

        return items;
    }

    /** flatten array like Structure of Array Like Structures to a simple Array (from real Arrays, Observable Arrays, ...) */
    export function FlattenArray<T>(unflatArray: ArrayLike<T>[]): T[];
    export function FlattenArray<T>(unflatArray: ArrayLike<T[]>): T[];
    export function FlattenArray<T>(unflatArray: ArrayLike<ArrayLike<T>>): T[];
    export function FlattenArray<T>(unflatArray: ArrayLike<ArrayLike<T>>): T[] {
        var result: T[] = [];

        forEach(unflatArray, (value, key) => {
            forEach(value, (innerValue, innerKey) => {
                result.push(innerValue);
            });
        });
        return result;
    }

    type PropertiesOfType<TObj, TResult> = { [K in keyof TObj]: TObj[K] extends TResult ? K : never }[keyof TObj];
    /** Flatten Tree to Array */
    export function FlattenTree<T>(items: T[], getChildren: PropertiesOfType<T, T[]>): T[];
    export function FlattenTree<T>(items: T[], getChildren: ((item: T) => T[])): T[];
    export function FlattenTree<T>(items: T[], getChildren: string | ((item: T) => T[])): T[] {
        getChildren = getter(getChildren);
        const result = new Array<T>(...items);
        let idx = -1;
        while (++idx < result.length) {
            result.push(...(getChildren(result[idx]) || []));
        }
        return result;
    }

    function forEach<T>(items: ArrayLike<T>, cb: (item: T, idx: number) => void) {
        var len = items && items.length;
        if (len > 0) {
            for (var i = 0; i < len; i++) {
                cb(items[i], i);
            }
        }
    }

    /** Find (first) index of item in Array like Object (that might not be a real Array)
     * @param items the array like obj
     * @param find the element to find first index for
     */
    export function indexOf<T>(items: ArrayLike<T>, find: T) {
        var len = items && items.length;
        if (len > 0) {
            for (var i = 0; i < len; i++) {
                if (items[i] === find)
                    return i;
            }
        }
    }

    export function IsDate(val): val is Date {
        return Object.prototype.toString.call(val) === '[object Date]';
    }

    /** Problem right now : Date Value get transferred to Client as String */
    export function ToDate(val: any): Date {
        if (IsDate(val))
            return val;

        if (typeof val == "undefined" || val === null)
            return null;

        try {
            return new Date(Date.parse(val));
        } catch (err) {
            try {
                // Problem : ISO Date can not be parsed in IE9!
                return _dateFromStandardIsoString(val);

            } catch (err) { }
        }
    }

    /** Custom Iso Date Parsing (for special IE) Handling) */
    function _dateFromStandardIsoString(date) {
        if (typeof date != "string" || date == "")
            throw new Error("Unexpected Value for custom iso Date Parsing");
        var struct, minutesOffset = 0;
        var numericKeys = [1, 4, 5, 6, 7, 10, 11];
        // ES5 §15.9.4.2 states that the string should attempt to be parsed as a Date Time String Format string
        // before falling back to any implementation-specific date parsing, so that’s what we do, even if native
        // implementations could be faster
        //              1 YYYY                2 MM       3 DD           4 HH    5 mm       6 ss        7 msec        8 Z 9 ±    10 tzHH    11 tzmm
        if ((struct = /^(\d{4}|[+\-]\d{6})(?:-(\d{2})(?:-(\d{2}))?)?(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{3,7}))?)?(?:(Z)|([+\-])(\d{2})(?::(\d{2}))?)?)?$/.exec(date))) {
            // avoid NaN timestamps caused by “undefined” values being passed to Date.UTC
            for (var i = 0, k; (k = numericKeys[i]); ++i) {
                struct[k] = +struct[k] || 0;
            }

            // allow undefined days and months
            struct[2] = (+struct[2] || 1) - 1;
            struct[3] = +struct[3] || 1;

            if (struct[8] !== 'Z' && struct[9] !== undefined) {
                minutesOffset = struct[10] * 60 + struct[11];

                if (struct[9] === '+') {
                    minutesOffset = 0 - minutesOffset;
                }
            }

            return new Date(Date.UTC(struct[1], struct[2], struct[3], struct[4], struct[5] + minutesOffset, struct[6], struct[7]));
        }
        else {
            throw new Error("Not in expected format");
        }
    }

    /** Convert Date Value to "DateOnly" string representation  */
    export function ToDateString(val: Date) {
        if (val == null || val == undefined || !(typeof val.getDate == "function"))
            return <any>val;

        if (isNaN(val.getDate()))
            return "";
        return (1e15 + val.getDate() + "").slice(-2) + "." + ((1e15 + (val.getMonth() + 1) + "").slice(-2)) + "." + val.getFullYear();
    }

    /** Get the date without time value (set to 0:00:00) */
    export function GetDateWithoutTime(date: Date, useUTC: boolean = false): Date {
        if (date) {
            if (useUTC) {
                return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
            }
            else {
                return new Date(date.getFullYear(), date.getMonth(), date.getDate());
            }
        }

        return null;
    }

    const _MSPERDAY = 24 * 60 * 60 * 1000;
    export function GetDayDifference(checkDate: Date, refDate?: Date): number {
        var d1 = GetDateWithoutTime(refDate || new Date());
        var d2 = GetDateWithoutTime(checkDate);
        if (d1 && d2) {
            return Math.round((d1.getTime() - d2.getTime()) / _MSPERDAY);
        }

        return NaN;
    }

    /** shorten Text with '...' if longer than threshold, either from front or from back */
    export function textShorten(text: string, count: number, fromFront: boolean) {
        var text = (text || "");
        if (text.length && count > 3 && text.length > count - 3 && count > 3) {
            if (fromFront)
                return "..." + text.slice(text.length - count);
            else
                return text.slice(0, count - 3) + " ...";
        }
        return text;
    }

    export function IsValidLink(text: string) {
        return !!text && /^(http|ftp|https):\/\/[\w-]+(\.[\w-]+)*([\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?/.test("" + text);
    }

    /** Wrap a Function to be Executed only Once , even if called Multiple Times (to ensure release of Dependencies) */
    export function once<T extends Function>(fn: T) {
        var called = false;
        var result;
        return <T><any>function () {
            if (called)
                return result;
            called = true;
            return result = fn.apply(undefined, arguments);
        };
    }

    /** throw Error with given message */
    export function error(msg: string): any { throw new Error(msg); }

    /** validate Condition */
    export function validate(check: boolean, errorMsg: string) { if (!check) { error(errorMsg); } };

    /** filter array to unique values helper */
    export function distinct<T>(data: T[]): T[] {
        return data.filter(_onlyUnique);
    }
    var _onlyUnique = (value, index: number, self: any[]) => self.indexOf(value) === index;

    /** Remove Element if it is part of array */
    export function remove<T>(items: T[], item: T): boolean {
        var idx = items.indexOf(item);
        var result = idx >= 0;
        if (result) {
            items.splice(idx, 1);
        }
        return result;
    }

    /** simple clone via JSON */
    export function clone<T>(item: T) {
        return item && <T>JSON.parse(JSON.stringify(item));
    }

    /** Sort by mapping to number order, CAUTION Result = input, sortes in Input!*/
    export function sort<T>(items: T[], getOrd: (item: T) => number) {
        return items.sort((a, b) => getOrd(a) - getOrd(b));
    }

    /** Returns a function, that, as long as it continues to be invoked, will not
     * be triggered. The function will be called after it stops being called for
     * N milliseconds. If `immediate` is passed, trigger the function on the
     * leading edge, instead of the trailing.*/
    export function debounce(func: (...anyArgs) => void, wait: number, context?: any, immediate?: boolean) {
        var timeout;
        return function () {
            var context = context || this, args = arguments;
            var later = function () {
                timeout = null;
                if (!immediate) {
                    func.apply(context, args);
                }
            };
            var callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) func.apply(context, args);
        };
    };

    /** Simple throttle : allowing to call configure on leading and/or on tail */
    export function throttle(func: (...anyArgs) => void, wait: number, leading: boolean = true, tail: boolean = true) {
        var timeout, callLater;
        return function () {
            var context = context || this, args = arguments;
            var later = function () {
                timeout = null;
                if (callLater) {
                    callLater = false;
                    func.apply(context, args);
                }
            };

            var callNow = leading && !timeout;
            if (timeout && tail)
                callLater = true;

            clearTimeout(timeout);

            timeout = setTimeout(later, wait);
            if (callNow) func.apply(context, args);
        };
    }

    /** Ensure a function is called only once per wait interval (first is called sync)*/
    export function maxInterval(func: (...anyArgs) => void, wait: number) {
        var timeout: number;
        var first = true;
        return function () {
            var context = context || this, args = arguments;
            var later = function () {
                timeout = null;
                func.apply(context, args);
            };
            if (first) {
                first = false;
                later();
            }
            if (timeout)
                return;
            timeout = window.setTimeout(later, wait);
        };
    }

    /**
     * Get Absolute URL with trailing slash
     * @param forcePath : remove 'page' element (needed for e.g. WebApi Calls, but bad for generating SPA calls ...
     */
    export function GetAbsoluteUrlPrefix(forcePath?: boolean) {
        var result = window.location.protocol + "//" + window.location.host;

        if (window.location.pathname) {
            result += window.location.pathname;
            if (forcePath)
                result = result.replace(/\/[^\/]+$/, "\/"); // remove everything after last slash if not last character
        } else {
            result += "/";
        }

        return result;
    }

    // Opne Ext Link (Auto Close window);
    export function wOpenExtLink(url: string) {
        var w = window.open(url, "_blank", "close=true"); // wOut 3rd Parameter : fails to open in IE
        if(!url.startsWith("http")) {
            setTimeout(() => w && w.close(), 5000);
        }
    }

    //#region helper interfaces
    /** Helper Interface */
    export interface TFunc1<T1, TResult> {
        (t1: T1): TResult;
    }
    /** Helper Interface */
    export interface TFunc2<T1, T2, TResult> {
        (t1: T1, t2: T2): TResult;
    }
    /** Helper Interface */
    export interface TFunc3<T1, T2, T3, TResult> {
        (t1: T1, t2: T2, t3: T3): TResult;
    }
    //#endregion

    /** bind helper to Preserve Type inference */
    export function proxy<T extends Function>(bindFunction: T, thisArg: any): T;
    /** bind helper to Preserve Type inference for function with one Argument */
    export function proxy<T1, TResult>(bindFunction: TFunc1<T1, TResult>, thisArg: any, t1: T1): () => TResult;
    /** bind helper to Preserve Type inference for function with two Argument2 */
    export function proxy<T1, T2, TResult>(bindFunction: TFunc2<T1, T2, TResult>, thisArg: any, t1: T1, t2: T2): () => TResult;
    export function proxy<T1, T2, T3, TResult>(bindFunction: TFunc3<T1, T2, T3, TResult>, thisArg: any, t1: T1, t2: T2): (T3) => TResult;
    export function proxy<T1, T2, TResult>(bindFunction: TFunc2<T1, T2, TResult>, thisArg: any, t1: T1): (t2: T2) => TResult;
    export function proxy(bindFunction: Function, thisArg: any, ...args: any[]) {
        return bindFunction.bind(thisArg, ...args);
    }

    export function tryProxy<T extends Function>(bindFunction: T, thisArg: any): T;
    /** bind helper to Preserve Type inference for function with one Argument */
    export function tryProxy<T1, TResult>(bindFunction: TFunc1<T1, TResult>, thisArg: any): (T1) => TResult;
    /** bind helper to Preserve Type inference for function with two Argument2 */
    export function tryProxy<T1, T2, TResult>(bindFunction: TFunc2<T1, T2, TResult>, thisArg: any): (T1, T2) => TResult;
    export function tryProxy(bindFunction: Function, thisArg: any, ...args: any[]): any {
        var p = bindFunction.bind(thisArg, ...args);
        return (...iargs) => {
            try {
                return p(...iargs);
            } catch (err) {
            };
        };
    }

    /** Parse Query String or Url */
    export function parseQueryString(urlOrQueryString: string) {
        // remove any preceding url and split
        var qParams = urlOrQueryString.substring(urlOrQueryString.indexOf('?') + 1).split('&'),
            d = decodeURIComponent;
        // march and parse
        return qParams.reduceRight((result, c) => {
            var pair = c.split('=');
            result[pair[0]] = pair[1];
            return result;
        }, <{ [name: string]: string }>{});
    }

    //#region Error Handling

    /** Validate Item is not a Server Error */
    export function resolveToError(serverItem, throwOnError?: boolean): any {
        var handleReturn = (error: any, message: string, details?: string) => {
            if (throwOnError == true) {
                throw new Error(message);
            }
            return message ? message : error;
        };

        // Handle "Our Internal" Error Convention
        var check = resolveInternalError(serverItem);
        if (check) {
            return handleReturn(check.ErrorMessage, check.ErrorMessage, check.ErrorDetails);
        }

        if (serverItem) {
            let itemStatus = serverItem.status,
                itemText = serverItem.statusText;
            if (itemStatus == -1 && !itemText) {
                var config = serverItem.config;
                let info = config.url || JSON.stringify(serverItem);
                return handleReturn(serverItem, "Die Anfrage konnte nicht bearbeitet werden oder wurde aufgrund eines Timeouts abgebrochen:\n" + info);
            }
            else if (itemStatus && itemText) {
                if (serverItem.statusText != "OK" && itemText != "load") {
                    if (itemStatus == "404" && serverItem.config && serverItem.config.url)
                        return handleReturn(serverItem, itemStatus + ":" + itemText + "[" + serverItem.config.url + "]", "");
                    if (itemStatus == "500" && (typeof serverItem.responseText == "string")) {
                        try {
                            var info = JSON.parse(serverItem.responseText);
                        } catch (err) { }
                        var msg;
                        if (info && (msg = info.ExceptionMessage || info.Message)) {
                            return handleReturn(serverItem, msg, info.StackTrace || info.Message);
                        }
                    }
                    var statusText = serverItem.statusText;
                    if (serverItem.data && serverItem.data.ExceptionMessage)
                        statusText = serverItem.data.ExceptionMessage;
                    return handleReturn(serverItem, itemStatus + ":" + statusText, "");
                }
            }

            if (serverItem.description || serverItem.message) {
                return handleReturn(serverItem, serverItem.message || serverItem.description, serverItem.description || serverItem.message);
            }
        }
        return serverItem;
    }

    /** Internal Errror Description Interface : any result from our WebAPI - controllers might
     * be of this form and then must be treated as Error
    */
    interface IErrorDescription {
        IsError: boolean;
        ErrorMessage: string;
        ErrorDetails: string;
    }

    /** Check for Internal ErrorDescription */
    function resolveInternalError(check: any): IErrorDescription {
        if (!check)
            return;
        var x = <IErrorDescription>check;
        if (x && x.IsError == true && typeof (x.ErrorMessage == "string"))
            return x;

        return resolveInternalError(check.data);
    }

    /** Handle Action in a Manner notyifing user when error Occurs*/
    export function UISafeAction<T>(action: () => T, noRethrow?: boolean) {
        try {
            return action();
        } catch (err) {
            if (typeof err == "string")
                err = new Error(err);
            if (!err._infomaHandled) {
                err._infomaHandled = true;
                // tslint:disable:ban
                alert("Fehler:\n" + resolveToError(err, false) + "\n" + (err.stack || ""));
            }
            if (!noRethrow)
                throw err;
        }
    }

    /** User Notification about Failure of Action, implicitly handles nested items : i.e. nested error will only be notified once */
    export function UISafe<T>(action: () => T): T;
    /** User Notification about Failure of Promise, implicitly handles nested items : i.e. nested error will only be notified once */
    export function UISafe<T extends PromiseLike<any>>(promise: T): T;
    export function UISafe(item: any) {
        if (isFunction(item)) {
            var func = <() => any>item;
            return UISafeAction(func);
        } else if (isPromise(item)) {
            return UISafePromise(item);
        } else {
            if (!item)
                alert("Fataler Fehler : Fehlerhafte Benutzung von UISafe: no Argument");
            else {
                var methods = [];
                for (var m in item) {
                    if (typeof item[m] == "function") {
                        methods.push(m);
                    }
                }
                alert(methods.join(","));
                alert("Fataler Fehler : Fehlerhafte Benutzung von UISafe: " + JSON.stringify(item) + methods.join(","));
            }
        }
    }

    function UISafePromise<T>(asPromise: PromiseLike<T>) {
        if (!isPromise(asPromise))
            return asPromise;

        asPromise["_infomaHandled"] = asPromise.then(null, err => {
            if (typeof err == "string")
                err = new Error(err);
            else if (error.IsCancel(err))
                throw err;
            if (!err["_infomaHandled"]) {
                err["_infomaHandled"] = true;
                if (!error.IsCancel(err)) {
                    var message = resolveToError(err, false);
                    if (typeof message != "string")
                        message = JSON.stringify(err);
                    alert(message);
                }
            }
        });
        return asPromise;
    }

    /** Set Explicit ErrorMessage for Failure of Promise */
    export function checkPromise<T>(message: string, promise: PromiseLike<T>) {
        if (!promise || !message)
            return promise;

        return promise.then(d => {
            try {
                resolveToError(d && d["Data"], true);
            } catch (e) {
                throw new Error(message + " - " + resolveToError(e, false));
            }
            return d;
        }, err => {
            throw new Error(message + " - " + resolveToError(err, false));
        });
    }

    export function isOperation(item: any): item is IInfOperation {
        return item && isFunction(item.Execute);
    }

    export function isPromise(item: any): item is PromiseLike<any> {
        if (item && (typeof item["then"] == "function"))
            return item;

        return null;
    }

    export function isFunction(f): f is Function { return typeof f == "function"; }
    function isArray(f) {
        return Array.isArray(f);
    }

    export function isObject(value) {
        return value !== null && typeof value === 'object';
    }

    function isPlainObject(o) {
        return typeof o == 'object' && o.constructor == Object;
    }

    export namespace error {
        /** Throw Error that gets implicitly suppressed for User Display */
        export function ThrowCancel(err?: Error | string): any {
            if (typeof err == "string")
                err = new Error(<string>err);
            var result: ICustomError = err || new Error("__Ignore");
            result.__Ignore = true; // "__Ignore" : handled in UISafe
            result.__Explicit = true;
            throw result;
        }

        /** Check if error is internal Cancel */
        export function IsCancel(err: Error | string) {
            if (typeof err == "string")
                return (<string><any>err) == "__Ignore";

            return err && err["__Ignore"] == true;
        }

        /** Util to Throw Error "gracefully" rejecting a Promise, signaling no User notification is necessaray) */
        export function UICancel(msg?: string | Error) {
            if (typeof msg === "string")
                var err = new Error(msg);
            else
                var err = (msg && "message" in <Error>msg) ? <Error>msg : new Error("__Ignore");
            err["__Ignore"] = true;
            throw err;
        }

        export interface ICustomError extends Error {
            __Explicit?: boolean;
            __Ignore?: boolean;
        }

        /** Create An Explicit Error  */
        export function ExplicitError(msg: string | Error, asIgnore?: boolean) {
            let result: ICustomError;
            if (msg instanceof Error) {
                result = msg;
            }
            else if (typeof msg === "string") {
                result = Error(msg);
            } else {
                result = new Error(fn.resolveToError(msg));
            }

            result.__Explicit = true;
            if (asIgnore === true) {
                result.__Ignore = true;
            }

            return <ICustomError>result;
        }

        /** Marker Error got notified to user */
        // TODO unity with __Ignore
        export function IsUINotified(err: any): err is (ICustomError & {
            /** UI Notified in out context */_infomaHandled?: true;
        }) {
            return err && err._infomaHandled == true;
        }

        /** Re- Throw an explicitly created error */
        export function IsExplicit(err: any): err is ICustomError {
            const check: ICustomError = err;
            return check && check.__Explicit === true;
        }

        export function ThrowIfExplicit(check: any) {
            if (IsExplicit(check)) {
                throw check;
            }
        }
    }

    /**
    * Extend Object by Properties from Template
    * - NOT overwriting existing properties (opposed to jQuery.extend)
    * - recursive, returns true if a change took place
    */
    export function ExtendObject<TItem = any>(target: Partial<TItem>, template: Partial<TItem>): boolean {
        _tempIsChanged = false;
        extendProperties(true, target, template);
        return _tempIsChanged;
    }
    var _tempIsChanged: boolean;

    /** Extend Properties : Copied from jQuery.extend, but keeping existing Properties */
    function extendProperties(...args) {
        var isChanged = false;
        var src, copyIsArray, copy, name, options, clone,
            target = arguments[0] || {},
            i = 1,
            length = arguments.length,
            deep = false;

        // Handle a deep copy situation
        if (typeof target === "boolean") {
            deep = target;
            target = arguments[1] || {};
            // skip the boolean and the target
            i = 2;
        }

        // Handle case when target is a string or something (possible in deep copy)
        if (typeof target !== "object" && !isFunction(target)) {
            target = {};
        }

        // extend jQuery itself if only one argument is passed
        if (length === i) {
            target = this;
            --i;
        }

        for (; i < length; i++) {
            // Only deal with non-null/undefined values
            if ((options = arguments[i]) != null) {
                // Extend the base object
                for (name in options) {
                    src = target[name];
                    copy = options[name];

                    // Prevent never-ending loop
                    if (target === copy) {
                        continue;
                    }

                    // Recurse if we're merging plain objects or arrays
                    if (deep && copy && (isPlainObject(copy) || (copyIsArray = isArray(copy)))) {
                        if (copyIsArray) {
                            copyIsArray = false;
                            // do not extend (empty) Array if available
                            if (isArray(src))
                                continue;
                            clone = src && isArray(src) ? src : [];
                        } else {
                            clone = src && isPlainObject(src) ? src : {};
                        }
                        if (clone !== src)
                            isChanged = true;
                        // Never move original objects, clone them
                        target[name] = extendProperties(deep, clone, copy);

                        // Don't bring in undefined values
                    } else if (copy !== undefined) {
                        // Do not overwrite Existing values --> opposed to jquery.Extend
                        if (target[name] == undefined) {
                            target[name] = copy;
                            if (!isChanged && !isArray(target))
                                isChanged = true;
                        }
                    }
                }
            }
        }

        // Return the modified object
        if (_tempIsChanged == false && isChanged) {
            _tempIsChanged = isChanged;
        }
        return target;
    };

    /** Notifications Util (probably is there a Standard/ Simpler way) */
    export class Notifications<T> {
        private callbacks = new Array<(items: T) => any>();

        /** bind callback to Notification  */
        public bind(callback: (item: T) => any) {
            var callbacks = this.callbacks;
            callbacks.push(callback);

            return () => {
                var idx = callbacks.indexOf(callback);
                if (idx >= 0)
                    callbacks.splice(idx, 1);
            };
        }

        /** trigger registered callbacks */
        trigger(item: T) {
            var callbacks = this.callbacks;
            callbacks.slice(0, callbacks.length).forEach(cb => cb(item));
        }
    }

    //#endregion
    /** reorder Items in Column Order for given colCount */
    export function reorderForColumns<T>(items: T[], colCount: number) {
        if (!(colCount > 1))
            return items;
        var result = new Array<T>(),
            currId: number,
            rowCount = Math.ceil(items.length / colCount);

        for (var i = 0; i < rowCount; i++) {
            for (var j = 0; j < colCount; j++) {
                if ((currId = j * rowCount + i) < items.length)
                    result.push(items[currId]);
            }
        }
        return result;
    }

    /** Split Amount, uneven amount to last */
    export function splitAmount(amount: number | string, num: number): number[] {
        if (typeof amount === "string") {
            amount = parseFloat(amount);
        }
        if (isNaN(amount)) {
            return;
        }

        var def = parseFloat((amount / num).toFixed(2));

        var result = new Array(num);
        for (var i = 0; i < num; i++) {
            result[i] = def;
        }

        var rem = amount - def * num;
        // 'round' after adding! (e.g 33.33 + 0.01 = 33.339999999999996 in js)
        result[num - 1] = parseFloat((rem + result[num - 1]).toFixed(2));
        return result;
    }
}


export function base64toBlob(base64Data: string, contentType: string) {
    contentType = contentType || '';
    var sliceSize = 1024;
    var byteCharacters = atob(base64Data);
    //var byteCharacters = decodeURIComponent(escape(window.atob(base64Data)))
    var bytesLength = byteCharacters.length;
    var slicesCount = Math.ceil(bytesLength / sliceSize);
    var byteArrays = new Array(slicesCount);

    for (var sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        var begin = sliceIndex * sliceSize;
        var end = Math.min(begin + sliceSize, bytesLength);

        var bytes = new Array(end - begin);
        for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
            bytes[i] = byteCharacters[offset].charCodeAt(0);
        }
        byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
}

export function downloadFile(newBlob: Blob, fileName: string) {
    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(newBlob, fileName);
    } else {
        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const data = window.URL.createObjectURL(newBlob);
        var link = document.createElement('a');
        link.href = data;
        link.download = fileName;
        link.click();
        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(() => window.URL.revokeObjectURL(data), 100);
    }
}

/** Utility to Create an "ActiveAware" "Counter" Wrapper */
export function CreateCounter<T>(thisArg?: any) {
    var idx = 0;
    return {
        /** Is a wrapped Operation Running at the Moment? */
        IsRunning: (count?: number) => idx > (count > 0 ? count : 0),
        /**  Returns true if Action is "FullStart" */
        Wrap: (action: () => void) => {
            var result = idx == 0;
            idx++;
            try {
                thisArg ? action.call(thisArg) : action();
            } finally {
                idx--;
            }
            return result;
        },
        /** Wraps a promise to this Counter */
        WrapPromise: <TPromise extends PromiseLike<any>>(promise: TPromise) => {
            idx++;
            try {
                const decrement = () => idx--;
                promise.then(decrement, decrement);
            } catch (exc) {
                idx--;
                throw exc;
            }
            return promise;
        }
    };
}

/** Utility to HtmlEncode text */
export function htmlEncode(value: string) {
    const el = document.createElement('a');
    el.appendChild(document.createTextNode(value));
    return el.innerHTML;
}
